<template>
  <v-card outlined class="rounded-lg fill-height">
    <v-card-text class="pa-4">

      <!-- Header -->
      <div class="d-flex align-center mb-4">
        <v-avatar size="40" color="primary" class="mr-3" rounded>
          <v-icon color="white">mdi-target-account</v-icon>
        </v-avatar>
        <div>
          <div class="subtitle-1 font-weight-bold">Growth Areas</div>
          <div class="caption grey--text">Personal &amp; manager-suggested goals</div>
        </div>
        <v-spacer />
        <v-btn
          color="primary"
          depressed
          small
          rounded
          class="white--text text-capitalize font-weight-bold"
          @click="openAiPanel"
        >
          <v-icon small class="mr-1">mdi-shimmer</v-icon>
          AI Suggest
        </v-btn>
      </div>

      <!-- AI Suggest Panel -->
      <v-expand-transition>
        <div v-if="showAiPanel" class="mb-4">
          <div class="rounded-lg pa-3 grey lighten-4">
            <div class="caption font-weight-bold mb-3 grey--text text--darken-1" style="letter-spacing:0.08em;">
              AI SUGGESTED GROWTH AREAS
            </div>
            <div v-if="aiSuggestionsLoading" class="d-flex align-center justify-center py-4">
              <v-progress-circular indeterminate size="20" width="2" color="primary" class="mr-2" />
              <span class="caption grey--text">Generating suggestions...</span>
            </div>
            <div
              v-for="(sug, i) in aiSuggestions"
              :key="i"
              class="d-flex align-start mb-3 pb-3"
              :style="i < aiSuggestions.length - 1 ? 'border-bottom:1px solid #e0e0e0;' : ''"
            >
              <v-icon small color="primary" class="mr-2 mt-1">mdi-shimmer</v-icon>
              <div class="flex-grow-1 mr-2">
                <div class="body-2 font-weight-medium">{{ sug.text }}</div>
                <div class="caption grey--text mt-1" style="font-style:italic;">{{ sug.reason }}</div>
              </div>
              <v-btn
                small
                depressed
                color="primary"
                rounded
                class="white--text text-capitalize flex-shrink-0"
                style="min-width:60px;"
                @click="addFromSuggestion(sug)"
              >
                + Add
              </v-btn>
            </div>
            <div v-if="aiSuggestions.length > 0 && !aiSuggestionsLoading" class="d-flex justify-center mt-1">
              <v-btn
                small
                text
                color="primary"
                class="text-capitalize"
                :loading="aiSuggestionsLoading"
                @click="regenBulkAreaSuggestions"
              >
                <v-icon small class="mr-1">mdi-refresh</v-icon>
                Generate new suggestions
              </v-btn>
            </div>
          </div>
        </div>
      </v-expand-transition>

      <!-- Growth Items -->
      <div v-if="growthItems.length === 0" class="caption grey--text text-center py-4">
        No growth areas yet. Add one below.
      </div>

      <div v-for="item in growthItems" :key="item.id">
        <!-- Item row -->
        <div
          class="pa-2 rounded-lg mb-1"
          :style="selectedItemId === item.id
            ? 'background:#e3f2fd;border:1px solid #90caf9;'
            : item.done
              ? 'background:rgba(76,175,80,0.07);border:1px solid rgba(76,175,80,0.25);'
              : 'background:#f5f5f5;border:1px solid transparent;'"
        >
          <div class="d-flex align-center">
            <v-icon
              small
              class="mr-2 flex-shrink-0"
              :color="item.done ? 'success' : 'grey'"
              style="cursor:pointer;"
              @click.stop="toggleDone(item)"
            >
              {{ item.done ? 'mdi-check-circle' : 'mdi-circle-outline' }}
            </v-icon>
            <span
              v-if="!item.aiReason"
              class="body-2 flex-grow-1"
              :class="item.done ? 'grey--text' : ''"
              :style="item.done ? 'text-decoration:line-through;' : ''"
            >{{ item.text }}</span>
            <div v-else class="flex-grow-1">
              <div
                class="body-2"
                :class="item.done ? 'grey--text' : ''"
                :style="item.done ? 'text-decoration:line-through;' : ''"
              >{{ item.text }}</div>
              <div class="caption primary--text" style="font-style:italic;">{{ item.aiReason }}</div>
            </div>
            <v-chip x-small color="amber lighten-4" class="ml-2 amber--text text--darken-3 flex-shrink-0 font-weight-bold">
              +50 pts
            </v-chip>
            <!-- AI suggest button -->
            <v-btn
              x-small
              depressed
              :color="selectedItemId === item.id ? 'primary' : 'blue lighten-4'"
              :class="selectedItemId === item.id ? 'white--text' : 'primary--text'"
              class="ml-1 rounded-lg"
              style="min-width:0;padding:0 8px;"
              :loading="itemAiLoadingId === item.id"
              :disabled="itemAiLoadingId !== null"
              @click="toggleSuggestion(item)"
            >
              <v-icon x-small>mdi-shimmer</v-icon>
            </v-btn>
            <!-- Delete button -->
            <v-btn
              x-small
              depressed
              color="red lighten-4"
              class="ml-1 red--text text--darken-2 rounded-lg"
              style="min-width:0;padding:0 8px;"
              @click="removeGrowth(item.id)"
            >
              <v-icon x-small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
          <div v-if="item.source === 'manager'" class="caption amber--text text--darken-2 mt-1 pl-6">
            Suggested by manager
          </div>
        </div>

        <!-- Inline AI Suggestion -->
        <v-expand-transition>
          <div
            v-if="selectedItemId === item.id"
            class="mb-2 mx-1 pa-3 rounded-lg blue lighten-5"
            style="border:1px solid #90caf9;"
          >
            <div class="caption font-weight-bold mb-1 grey--text text--darken-1" style="letter-spacing:0.08em;">
              AI SUGGESTION
            </div>
            <div v-if="itemAiLoadingId === item.id" class="d-flex align-center py-1">
              <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
              <span class="caption grey--text">Generating suggestion...</span>
            </div>
            <template v-else-if="item.aiSuggestion">
              <div class="body-2 font-weight-medium mb-1">{{ item.aiSuggestion.text }}</div>
              <div class="caption grey--text mb-2" style="font-style:italic;">{{ item.aiSuggestion.reason }}</div>
              <div class="d-flex align-center" style="gap:8px;">
                <v-btn
                  x-small
                  color="primary"
                  depressed
                  rounded
                  class="white--text text-capitalize"
                  @click="useInlineSuggestion(item)"
                >
                  <v-icon x-small class="mr-1">mdi-check</v-icon>
                  Use this
                </v-btn>
                <v-btn
                  x-small
                  depressed
                  rounded
                  color="blue lighten-3"
                  class="white--text text-capitalize"
                  :loading="itemAiLoadingId === item.id"
                  @click="regenAreaSuggestion(item)"
                >
                  <v-icon x-small class="mr-1">mdi-refresh</v-icon>
                  Generate new
                </v-btn>
                <v-btn x-small text class="text-capitalize grey--text" @click="selectedItemId = null">
                  × Dismiss
                </v-btn>
              </div>
            </template>
          </div>
        </v-expand-transition>
      </div>

      <!-- Add input -->
      <div class="mt-3">
        <div class="d-flex align-center" style="gap:8px;">
          <v-text-field
            v-model="newGrowth"
            placeholder="Name a skill or capability to develop (e.g. delegation, executive communication)..."
            dense
            outlined
            hide-details
            class="flex-grow-1 rounded-lg"
            @keyup.enter="addGrowth"
          />
          <v-btn
            x-small
            depressed
            color="primary"
            class="ml-1 white--text rounded-lg"
            style="min-width:0;padding:0 8px;height:36px;"
            :loading="aiEnhancing"
            :disabled="!newGrowth.trim() || aiEnhancing"
            @click="aiEnhance"
          >
            <v-icon small>mdi-shimmer</v-icon>
          </v-btn>
        </div>
        <div class="d-flex justify-end mt-2">
          <v-btn
            small
            depressed
            color="primary"
            rounded
            class="white--text text-capitalize"
            :disabled="!newGrowth.trim()"
            @click="addGrowth"
          >
            <v-icon small class="mr-1">mdi-plus</v-icon>
            Add
          </v-btn>
        </div>
        <!-- AI enhancing spinner -->
        <div v-if="aiEnhancing" class="mt-2 pa-3 rounded-lg blue lighten-5 d-flex align-center" style="border:1px solid #90caf9;">
          <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" />
          <span class="caption grey--text">Enhancing your goal...</span>
        </div>

        <!-- AI enhanced preview -->
        <v-expand-transition>
          <div
            v-if="aiEnhancedText && !aiEnhancing"
            class="mt-2 pa-3 rounded-lg blue lighten-5"
            style="border:1px solid #90caf9;"
          >
            <div class="caption font-weight-bold mb-1 grey--text text--darken-1" style="letter-spacing:0.08em;">AI SUGGESTION</div>
            <div class="body-2 font-weight-medium mb-1">{{ aiEnhancedText }}</div>
            <div class="caption grey--text mb-2" style="font-style:italic;">Converted into a specific, actionable growth goal.</div>
            <div class="d-flex align-center" style="gap:8px;">
              <v-btn x-small color="primary" depressed rounded class="white--text text-capitalize" @click="applyEnhanced">
                <v-icon x-small class="mr-1">mdi-check</v-icon> Use this
              </v-btn>
              <v-btn x-small text class="text-capitalize grey--text" @click="aiEnhancedText = ''">× Dismiss</v-btn>
            </div>
          </div>
        </v-expand-transition>
      </div>

    </v-card-text>
  </v-card>
</template>

<script>
import { askAI, parseAIJson, buildUserContext } from '@/services/growthAiService';

// Shared role-ID → profile map (matches AiCoachCard / HabitTrackerCard).
// 1 Super Admin | 2 Admin | 3 Agent | 4/5/9 Dev | 6 Marketing | 7 Design
// 8 Sales | 10 Recruitment | 11 Management | 12 Operations | 13 Finance
const ID_TO_PROFILE = {
  1: 'superadmin', 2: 'management', 3: 'agent',
  4: 'engineering', 5: 'engineering', 9: 'engineering',
  6: 'marketing', 7: 'design', 8: 'sales',
  10: 'recruitment', 11: 'management', 12: 'operations', 13: 'finance',
};
const PROFILE_PRIORITY = {
  superadmin: 100, management: 90, sales: 70, agent: 65, engineering: 60,
  recruitment: 55, marketing: 50, design: 45, operations: 40, finance: 35, generic: 0,
};

export default {
  name: 'GrowthAreasCard',
  inject: {
    personalTasks: { default: () => () => [] },
    assignedTasks: { default: () => () => [] },
    allManagers: { default: () => () => [] },
    roles: { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      newGrowth: '',
      showAiPanel: false,
      selectedItemId: null,
      itemAiLoadingId: null,
      aiEnhancedText: '',
      aiEnhancing: false,
      aiSuggestionsLoading: false,
      growthItems: [],
      aiSuggestions: [],
      // Loaded on mount so every AI call can ground suggestions in real KPIs.
      kpis: [],
      habits: [],
      // Pillar scores (0-100) pulled from /api/employee-growth/score so we can
      // feed real KPI%, task%, AI%, habit% into the AI prompt per DG spec.
      pillars: { kpi: 0, tasks: 0, ai: 0, habits: 0 },
    };
  },
  computed: {
    storeUser() { return this.$store.state.auth?.user || {}; },
    userRoleIds() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.id).filter(id => id != null);
    },
    userRoleNames() {
      const rm = Array.isArray(this.storeUser.role_members) ? this.storeUser.role_members : [];
      return rm.map(m => m.role?.name).filter(Boolean);
    },
    managedRoleIds() {
      const uid = this.storeUser.id;
      const managers = typeof this.allManagers === 'function' ? this.allManagers() : [];
      if (!Array.isArray(managers) || !uid) return [];
      return managers
        .filter(m => m.user_id === uid && (m.is_manager === true || m.is_manager === 1 || m.is_manager === undefined))
        .map(m => m.user_role_id)
        .filter(id => id != null);
    },
    managedRoleNames() {
      const rolesList = typeof this.roles === 'function' ? this.roles() : [];
      if (!Array.isArray(rolesList)) return [];
      return this.managedRoleIds
        .map(id => rolesList.find(r => r.id === id)?.name)
        .filter(Boolean);
    },
    roleProfile() {
      const ids = new Set(this.userRoleIds);
      const legacy = this.storeUser.role_id;
      if (legacy === 1) ids.add(1);
      if (legacy === 11) ids.add(11);
      if (legacy === 2) ids.add(2);
      const profiles = new Set();
      ids.forEach(id => { const p = ID_TO_PROFILE[id]; if (p) profiles.add(p); });
      if (this.managedRoleIds.length > 0) profiles.add('management');
      const arr = Array.from(profiles).sort(
        (a, b) => (PROFILE_PRIORITY[b] || 0) - (PROFILE_PRIORITY[a] || 0)
      );
      return arr[0] || 'generic';
    },
    primaryRoleName() {
      return this.userRoleNames[0] || (this.storeUser.role_id === 1 || this.storeUser.role_id === 11
        ? 'Admin' : this.storeUser.role_id === 2 ? 'Manager' : 'Employee');
    },
    allTasks() {
      const p = typeof this.personalTasks === 'function' ? this.personalTasks() : [];
      const a = typeof this.assignedTasks === 'function' ? this.assignedTasks() : [];
      return [...p, ...a].filter(t => !t.deleted_at);
    },
    todayStr() { return new Date().toISOString().slice(0, 10); },
    overdueTasks() {
      const today = this.todayStr;
      return this.allTasks.filter(t => !t.completed_at && t.due_date && String(t.due_date).slice(0, 10) < today);
    },
    inProgressTasks() {
      return this.allTasks.filter(t => !t.completed_at && (t.status?.name || '').toLowerCase().includes('progress'));
    },
    completedThisWeek() {
      const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      return this.allTasks.filter(t => t.completed_at && String(t.completed_at).slice(0, 10) >= weekAgo);
    },
    pendingKpis() {
      return (this.kpis || []).filter(k => !k.is_done);
    },
    doneKpis() {
      return (this.kpis || []).filter(k => k.is_done);
    },
  },
  mounted() {
    this.load();
    this.loadSideContext();
    // Sync with global growth bus so areas list + pillar context refresh when
    // other cards mutate data (e.g. task completion, KPI toggles, habit ticks).
    this.$root.$on('growth:refresh', this.onGrowthRefresh);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.onGrowthRefresh);
  },
  methods: {
    onGrowthRefresh() {
      this.load();
      this.loadSideContext();
    },
    normalizeArea(a) {
      return { id: a.id, text: a.text, aiReason: a.ai_reason || null, source: a.source, done: !!a.is_done, aiSuggestion: null };
    },
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/areas');
        if (res.data.response) {
          this.growthItems = res.data.data.map(this.normalizeArea);
        }
      } finally {
        this.loading = false;
      }
    },
    // Load KPIs, habits, and pillar scores so AI suggestions are anchored on
    // the real DG-spec metrics (KPI %, Task %, AI %, Habit %).
    async loadSideContext() {
      try {
        const [kpisRes, habitsRes, scoreRes] = await Promise.all([
          this.$axios.get('api/employee-growth/kpis').catch(() => null),
          this.$axios.get('api/employee-growth/habits').catch(() => null),
          this.$axios.get('api/employee-growth/score').catch(() => null),
        ]);

        if (kpisRes?.data?.response) {
          const payload = kpisRes.data.data || {};
          this.kpis = Array.isArray(payload)
            ? payload
            : [...(payload.this_week || []), ...(payload.last_week || [])];
        }

        if (habitsRes?.data?.response) {
          this.habits = Array.isArray(habitsRes.data.data) ? habitsRes.data.data : [];
        }

        if (scoreRes?.data?.response) {
          const d = scoreRes.data.data || {};
          this.pillars = {
            kpi:    d.kpi_score    ?? d.pillar_scores?.kpi    ?? 0,
            tasks:  d.task_score   ?? d.pillar_scores?.tasks  ?? 0,
            ai:     d.ai_score     ?? d.pillar_scores?.ai     ?? 0,
            habits: d.habit_score  ?? d.pillar_scores?.habits ?? 0,
          };
        }
      } catch (e) { /* non-critical */ }
    },
    // Bucket a 0-100 pillar score into a qualitative band so the AI can apply
    // the DG spec logic (low → foundational skills, high → stretch skills).
    pillarBand(v) {
      if (v >= 80) return 'high';
      if (v >= 50) return 'moderate';
      return 'low';
    },
    // Build a DG-Ventures-spec signal block.
    //
    // PHILOSOPHY (per spec): growth areas are SKILLS/CAPABILITIES, not tasks.
    // The AI must NOT output to-do items, numbers, or deadlines. It should
    // read these signals and infer which CAPABILITY BUCKET the user needs:
    //   - KPI low          → performance-improvement skills
    //   - Task % low       → execution & prioritization skills
    //   - Habit % low      → discipline & consistency skills
    //   - AI usage low     → AI fluency & leverage skills
    //   - All high         → stretch / leadership / strategic skills
    buildGrowthSignalBlock() {
      const profile = this.roleProfile;
      const p = this.pillars;
      const band = this.pillarBand;
      const signals = [];

      // --- AUTO-PROVIDED PERFORMANCE PERCENTAGES (DG spec core inputs) ---
      signals.push(`- KPI ACHIEVEMENT: ${p.kpi}% (${band(p.kpi)})`);
      signals.push(`- TASK COMPLETION: ${p.tasks}% (${band(p.tasks)})`);
      signals.push(`- HABIT CONSISTENCY: ${p.habits}% (${band(p.habits)})`);
      signals.push(`- AI USAGE LEVEL: ${p.ai}% (${band(p.ai)})`);

      // --- WHICH CAPABILITY BUCKETS TO TARGET (derived from bands) ---
      const lowBuckets = [];
      if (band(p.kpi)    === 'low') lowBuckets.push('performance-improvement skills (how to set/hit measurable outcomes consistently)');
      if (band(p.tasks)  === 'low') lowBuckets.push('execution & prioritization skills (focus, scoping, follow-through)');
      if (band(p.habits) === 'low') lowBuckets.push('discipline & consistency skills (routines, commitment, self-management)');
      if (band(p.ai)     === 'low') lowBuckets.push('AI fluency & leverage (using AI tools to multiply output)');
      if (lowBuckets.length > 0) {
        signals.push(`- CAPABILITY GAPS TO TARGET: ${lowBuckets.join('; ')}.`);
      } else {
        signals.push(`- ALL PILLARS HEALTHY → target STRETCH capabilities: leadership, strategic thinking, teaching, cross-functional influence.`);
      }

      // --- LIGHT QUALITATIVE CONTEXT (no task names, no numbers in output) ---
      if (this.kpis.length === 0) {
        signals.push(`- NO KPIs SET → include a capability around "defining what good looks like" for a ${this.primaryRoleName}.`);
      }
      if (this.habits.length === 0) {
        signals.push(`- NO HABITS TRACKED → one capability can focus on building a personal operating rhythm.`);
      }
      if (this.managedRoleNames.length > 0) {
        signals.push(`- LEADERSHIP CONTEXT: manages ${this.managedRoleNames.join(', ')} team(s) → leadership/coaching capabilities are high-leverage.`);
      }

      // --- ROLE LANES — describes the craft, not the tasks ---
      const growthLanes = {
        superadmin: 'cross-functional leadership: strategic clarity, executive communication, delegation, escalation frameworks, org-wide decision-making',
        management: 'people leadership: coaching, delegation, 1-on-1 quality, giving hard feedback, team metric literacy, running effective meetings',
        sales: 'sales craft: discovery questioning, objection handling, negotiation, pipeline discipline, relationship-building',
        agent: 'client craft: empathy in tough cases, de-escalation, SLA awareness, knowledge authoring, escalation judgement',
        engineering: 'technical craft: code review quality, system design, debugging rigor, testing discipline, technical writing',
        marketing: 'marketing craft: copywriting, data literacy, campaign analytics, brief writing, experiment design',
        design: 'design craft: critique-giving, user research, design systems, prototyping speed, stakeholder communication',
        recruitment: 'recruiting craft: sourcing strategy, interviewing rigor, stakeholder management, offer negotiation, pipeline hygiene',
        operations: 'ops craft: process design, vendor management, SLA engineering, runbook authoring, cross-team communication',
        finance: 'finance craft: forecasting accuracy, audit readiness, stakeholder reporting, automation of recurring work',
        generic: 'universal: planning, communication, focus, continuous learning, stakeholder management',
      };
      signals.push(`- ROLE LANES (${profile}): ${growthLanes[profile] || growthLanes.generic}.`);

      return signals.join('\n');
    },
    async addGrowth() {
      const t = (this.aiEnhancedText || this.newGrowth).trim();
      if (!t) return;
      try {
        const res = await this.$axios.post('api/employee-growth/areas', { text: t, source: 'self' });
        if (res.data.response) {
          this.growthItems.push(this.normalizeArea(res.data.data));
          this.newGrowth = '';
          this.aiEnhancedText = '';
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to add area'); }
    },
    async removeGrowth(id) {
      try {
        await this.$axios.delete(`api/employee-growth/areas/${id}`);
        this.growthItems = this.growthItems.filter(i => i.id !== id);
        if (this.selectedItemId === id) this.selectedItemId = null;
      } catch (e) { this.$toast && this.$toast.error('Failed to delete area'); }
    },
    async toggleDone(item) {
      try {
        const res = await this.$axios.post(`api/employee-growth/areas/${item.id}`, { is_done: !item.done });
        if (res.data.response) {
          item.done = !item.done;
          this.$root.$emit('growth:refresh');
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to update'); }
    },
    async regenAreaSuggestion(item) {
      this.$set(item, 'aiSuggestion', null);
      await this.toggleSuggestion(item);
    },
    async regenBulkAreaSuggestions() {
      if (this.aiSuggestionsLoading) return;
      this.aiSuggestions = [];
      this.aiSuggestionsLoading = true;
      try {
        const existing = this.growthItems.map(i => i.text).join('; ') || 'none';
        const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthItems });
        const signals = this.buildGrowthSignalBlock();
        const system = `You are an AI performance coach for DG Ventures.
Generate exactly 4 personalized long-term growth areas (skills/capabilities) for a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Must be distinct from existing items: ${existing}
Return ONLY a JSON array with exactly 4 items: [{"text": "...", "reason": "..."}]
SIGNALS:
${signals}
USER CONTEXT:
${userCtx}`;
        const raw = await askAI(
          `Suggest 4 different long-term growth-area capabilities tailored to my role and performance. Existing: ${existing}.`,
          system,
          { logSession: false, isHighImpact: false },
        );
        this.aiSuggestions = parseAIJson(raw);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to refresh suggestions');
      } finally {
        this.aiSuggestionsLoading = false;
      }
    },
    async toggleSuggestion(item) {
      if (this.selectedItemId === item.id) {
        this.selectedItemId = null;
        return;
      }
      this.selectedItemId = item.id;
      if (item.aiSuggestion) return;
      this.itemAiLoadingId = item.id;
      try {
        const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthItems });
        const signals = this.buildGrowthSignalBlock();
        const system = `You are an AI performance coach for DG Ventures, coaching a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Rewrite the user's existing growth area into a better SKILL / CAPABILITY — long-term and developmental.

STRICT RULES (DG Ventures spec):
- DO NOT suggest tasks, to-dos, or action items.
- DO NOT mention specific deadlines, numbers, task names, or KPI names.
- Focus ONLY on SKILLS, CAPABILITIES, and PERFORMANCE IMPROVEMENT.
- Must be LONG-TERM and DEVELOPMENTAL, not a one-off action.
- Tailor to the user's ROLE and the CAPABILITY GAPS in SIGNALS:
    · KPI low      → performance-improvement skills
    · Task low     → execution & prioritization skills
    · Habit low    → discipline & consistency skills
    · AI low       → AI fluency & leverage skills
    · All healthy  → stretch / leadership / strategic skills
- The "text" is a capability to develop (e.g. "Sharpen discovery questioning", "Build delegation judgement", "Master structured communication under pressure").
- The "reason" explains in plain language why this capability matters for THIS user's role and performance band (no numbers).
- Max 14 words for "text". Max 18 words for "reason".

Return ONLY valid JSON: {"text": "capability to develop", "reason": "why this matters for this user (no numbers)"}

SIGNALS:
${signals}

USER CONTEXT:
${userCtx}`;
        // Log as high-impact AI session → awards AI-pillar points (spec: 5 pts, cap 20/day).
        const raw = await askAI(
          `Upgrade this growth area into a stronger capability: "${item.text}"`,
          system,
          { logSession: true, isHighImpact: true },
        );
        item.aiSuggestion = parseAIJson(raw);
      } catch (e) {
        this.$toast && this.$toast.error('Failed to get AI suggestion');
        this.selectedItemId = null;
      } finally {
        this.itemAiLoadingId = null;
      }
    },
    async useInlineSuggestion(item) {
      const newText = item.aiSuggestion.text;
      const newReason = item.aiSuggestion.reason;
      item.text = newText;
      item.aiReason = newReason;
      item.aiSuggestion = null;
      this.selectedItemId = null;
      try {
        await this.$axios.post(`api/employee-growth/areas/${item.id}`, { text: newText, ai_reason: newReason });
      } catch (e) {
        this.$toast && this.$toast.error('Failed to save updated area');
      }
    },
    async addFromSuggestion(sug) {
      try {
        const res = await this.$axios.post('api/employee-growth/areas', { text: sug.text, ai_reason: sug.reason, source: 'self' });
        if (res.data.response) {
          this.growthItems.push(this.normalizeArea(res.data.data));
          this.showAiPanel = false;
        }
      } catch (e) { this.$toast && this.$toast.error('Failed to add area'); }
    },
    async openAiPanel() {
      this.showAiPanel = !this.showAiPanel;
      if (this.showAiPanel && !this.aiSuggestionsLoading) {
        this.aiSuggestions = [];
        this.aiSuggestionsLoading = true;
        try {
          const existing = this.growthItems.map(i => i.text).join('; ') || 'none';
          const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthItems });
          const signals = this.buildGrowthSignalBlock();
          const system = `You are an AI performance coach for DG Ventures.
Generate exactly 4 personalized GROWTH AREAS for a ${this.primaryRoleName} (role profile: ${this.roleProfile}).

STRICT RULES (DG Ventures spec — follow exactly):
- DO NOT suggest tasks, to-do actions, or action items.
- DO NOT mention specific deadlines, numbers, task names, or KPI names.
- Focus ONLY on SKILLS, CAPABILITIES, and PERFORMANCE IMPROVEMENT.
- Suggestions must be LONG-TERM and DEVELOPMENTAL.
- Tailor every suggestion to the user's ROLE and their PERFORMANCE bands in SIGNALS.

HOW TO DECIDE WHAT TO SUGGEST (apply this logic, not surface it in output):
- If KPI band is low     → include a performance-improvement capability (e.g. "outcome framing", "metric ownership", "structured goal setting").
- If Task band is low    → include an execution & prioritization capability (e.g. "ruthless prioritization", "scoping discipline", "follow-through").
- If Habit band is low   → include a discipline/consistency capability (e.g. "routine design", "commitment management").
- If AI usage is low     → include an AI fluency capability (e.g. "AI-assisted drafting", "prompt craft for role tasks", "AI-leveraged analysis").
- If all bands are healthy → use stretch capabilities from ROLE LANES (leadership, strategic thinking, teaching, influence).
- The 4th slot: cover a craft skill from ROLE LANES that fits the user's profile.

OUTPUT RULES:
- Each "text" names ONE capability/skill to develop. 3–14 words. No verbs like "complete", "finish", "deliver". Prefer "Sharpen…", "Build…", "Strengthen…", "Develop…", "Deepen…".
- Each "reason" explains in plain language WHY this capability matters for THIS user's role and current performance — NO numbers, NO task names, max 18 words.
- MUST be distinct from existing items: ${existing}
- No generic platitudes like "improve communication" — name the type of communication and the context (e.g. "executive-level written clarity", "cross-team escalation communication").

Return ONLY a JSON array with exactly 4 items: [{"text": "...", "reason": "..."}, ...]

SIGNALS:
${signals}

USER CONTEXT:
${userCtx}`;
          // Log as high-impact AI session → awards AI-pillar points (spec: 5 pts, cap 20/day).
          const raw = await askAI(
            `Suggest 4 long-term growth-area capabilities tailored to my role and performance. Existing: ${existing}.`,
            system,
            { logSession: true, isHighImpact: true },
          );
          this.aiSuggestions = parseAIJson(raw);
        } catch (e) {
          this.$toast && this.$toast.error('Failed to load AI suggestions');
        } finally {
          this.aiSuggestionsLoading = false;
        }
      }
    },
    async aiEnhance() {
      const t = this.newGrowth.trim();
      if (!t || this.aiEnhancing) return;
      this.aiEnhancing = true;
      this.aiEnhancedText = '';
      try {
        const userCtx = buildUserContext(this, { kpis: this.kpis, habits: this.habits, growthAreas: this.growthItems });
        const signals = this.buildGrowthSignalBlock();
        const system = `You are an AI performance coach for DG Ventures, coaching a ${this.primaryRoleName} (role profile: ${this.roleProfile}).
Rewrite the user's typed growth area into a clearer SKILL / CAPABILITY — long-term and developmental.

STRICT RULES (DG Ventures spec):
- DO NOT turn it into a task, to-do, or action item.
- DO NOT add numbers, deadlines, task names, or KPI names.
- Focus on a SKILL or CAPABILITY that is long-term and developmental.
- Preserve the user's intent — do not swap the topic.
- Tailor wording to fit their ROLE and the capability gaps in SIGNALS.
- Max 14 words. One phrase or short sentence.
- Prefer verbs like "Sharpen", "Build", "Strengthen", "Develop", "Deepen", "Master".
- Return ONLY the rewritten phrase. No quotes, no prefix, no explanation, no markdown.

SIGNALS:
${signals}

USER CONTEXT:
${userCtx}`;
        // Log as high-impact AI session → awards AI-pillar points (spec: 5 pts, cap 20/day).
        this.aiEnhancedText = await askAI(t, system, { logSession: true, isHighImpact: true });
      } catch (e) {
        this.$toast && this.$toast.error('Failed to enhance text');
      } finally {
        this.aiEnhancing = false;
      }
    },
    applyEnhanced() {
      this.newGrowth = this.aiEnhancedText;
      this.aiEnhancedText = '';
    },
  },
};
</script>
